/* eslint-disable no-param-reassign */
import { isPtacSystemPreference } from '@alpinehomeair/common';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import store from 'app/store';
import { DesignRuleValidationIssue } from 'data/types';
import {
  setShouldAutoGenerateLevels,
  setShouldAutoGenerateSpaces
} from 'features/ai';
import {
  setActiveConfig,
  setConfigurationOptions,
  setConfigurations,
  setDesignRuleResults,
  syncConfigValid
} from 'features/configurations';
import { setProjectSettingValue, updateProjectSetting } from 'features/project';
import { resetPtacSelection } from 'features/ptac-selection/ptac-selection-slice';
import {
  clearFootprint,
  setExistingSystems,
  setStructure,
  setStructureName,
  setStructureSubtypeId,
  setStructureTypeId,
  setSupplementalMiniSplitSpaces
} from 'features/structure';
import {
  setActiveSystemType,
  setSystemOptions,
  updateSelectedDuctedSystem,
  updateSelectedSystem
} from 'features/systems';
import {
  defaultUIState,
  setComparisonTableData,
  setSystemFilters
} from 'features/ui';
import { produce } from 'immer';
import { useEffect, useRef } from 'react';
import { getQuotableAccessories } from 'utils/accessories';
import axios from 'utils/api';
import {
  compositionPicksSignature,
  configurationOptionSignature,
  existingEquipmentSignature,
  projectOptionInputsSignature,
  recommendationSelectionsSignature,
  resetInvalidatedSystemSelections,
  structureSelectionSignature
} from 'utils/compare-list-invalidation';
import { updateConfigZoneItems } from 'utils/configurations';
import { isPtacExistingSystem } from 'utils/equipment-preferences-complete';
import { asksHasExistingEquipment } from 'utils/existing-equipment-page-visibility';
import {
  getProjectSettingValue,
  isDuctedPathway,
  isEpOnlyPreference
} from 'utils/project';
import { updatePropertyAttributesFromAttomData } from 'utils/property';
import { notification } from 'utils/ui';
import { isErrorSeverity } from 'utils/validation';

type ResetDataWatcherProps = {
  loading: boolean;
};

let skipNextConfigReset = false;

export function setSkipNextConfigReset(value: boolean) {
  skipNextConfigReset = value;
}

export default function ResetDataWatcher({ loading }: ResetDataWatcherProps) {
  const dispatch = useAppDispatch();
  const { attomData, projectSettings, locationData, totalBTUs } =
    useAppSelector((state) => state.project);
  const { structureTypeId, structureSubtypeId } = useAppSelector(
    (state) => state.structure
  );

  const { configurations, configurationOptions, activeConfig } = useAppSelector(
    (state) => state.configurations
  );
  const activeConfiguration = configurations[activeConfig];
  const {
    systemOptions,
    selectedSystems,
    selectedDuctedSystems,
    systemsToCompare
  } = useAppSelector((state) => state.systems);
  const { activeTab } = useAppSelector((state) => state.ui);
  const previousSystemPreferenceRef = useRef<unknown>(undefined);
  const {
    structure,
    structureUniqueId,
    existingSystems,
    recommendationSelectionsBySystem,
    recommendationSelectionsLoaded,
    wantCompositionPicksBySystem,
    supplementalMiniSplitSpaces
  } = useAppSelector((state) => state.structure);
  const { preventAutoGenerateSpaces } = useAppSelector((state) => state.ai);

  // Previous-render references let the reset effects distinguish a
  // coordinated replacement (structure import / restore dispatches new
  // structure or configurations together with the state that triggers the
  // effect) from a user-initiated change that leaves stale data behind.
  // They are refreshed by the effect at the bottom of this component, which
  // must stay declared after the reset effects.
  const prevStructureRef = useRef(structure);
  const prevConfigurationsRef = useRef(configurations);
  // The app normally mounts this watcher during loading, so first-run
  // effects are no-ops (`!loading`). A post-load remount would otherwise
  // treat the live project as a user edit and clear structure,
  // configurations, and selected systems.
  const hasCompletedFirstMountRef = useRef(false);

  useEffect(() => {
    const structureReplacedThisRender = prevStructureRef.current !== structure;
    if (
      hasCompletedFirstMountRef.current &&
      !loading &&
      structure.length > 0 &&
      !structureReplacedThisRender
    ) {
      dispatch(setStructure([]));
      dispatch(clearFootprint());
      dispatch(setShouldAutoGenerateLevels(true));
      if (!preventAutoGenerateSpaces) {
        dispatch(setShouldAutoGenerateSpaces(true));
      }
      notification('Structure reset', 'warning');
    }
  }, [structureTypeId, structureSubtypeId]);

  useEffect(() => {
    if (skipNextConfigReset) {
      skipNextConfigReset = false;
      return;
    }
    const configurationsReplacedThisRender =
      prevConfigurationsRef.current !== configurations;
    if (
      hasCompletedFirstMountRef.current &&
      !loading &&
      configurations.length > 0 &&
      !configurationsReplacedThisRender
    ) {
      dispatch(setConfigurations([]));
      dispatch(setActiveConfig(0));
      notification('Configurations reset', 'warning');
    }
  }, [
    JSON.stringify(
      structure.map((l) => ({
        id: l.uniqueId,
        sections: l.sections.map((s) => ({
          id: s.uniqueId,
          spaces: s.spaces
            .map((sp) => ({
              id: sp.uniqueId,
              attributes: sp.attributes
                .filter((a) => a.code !== 'preferred_air_handler_type')
                .map((a) => ({
                  code: a.code,
                  value: a.value
                }))
            }))
            .sort((a, b) => a.id.localeCompare(b.id))
        }))
      }))
    ),
    getProjectSettingValue('system_preference')
  ]);

  useEffect(() => {
    if (
      hasCompletedFirstMountRef.current &&
      !loading &&
      (configurationOptions.length > 0 || configurations.length > 0)
    ) {
      dispatch(
        setConfigurationOptions(updateConfigZoneItems(configurationOptions))
      );
      dispatch(setConfigurations(updateConfigZoneItems(configurations)));
      notification('Configurations updated', 'warning');
    }
  }, [
    JSON.stringify(
      structure.flatMap((l) =>
        l.sections
          .flatMap((s) => s.spaces)
          .sort((a, b) => a.uniqueId.localeCompare(b.uniqueId))
          .map((sp) => ({
            label: sp.label,
            BTUs: sp.BTUs
          }))
      )
    )
  ]);

  const structureOptionsKey = structureSelectionSignature(structure);
  const existingEquipmentKey = existingEquipmentSignature(existingSystems);
  const configurationOptionsKey = configurationOptionSignature(configurations);
  const compositionPicksKey = compositionPicksSignature(
    wantCompositionPicksBySystem
  );
  const projectOptionInputsKey = projectOptionInputsSignature({
    projectSettings,
    zipCode: locationData?.zipcode,
    totalBTUs,
    supplementalMiniSplitSpaces
  });
  const recommendationSelectionsKey = recommendationSelectionsLoaded
    ? recommendationSelectionsSignature(recommendationSelectionsBySystem)
    : null;
  const recommendationSelectionsBaselineRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      hasCompletedFirstMountRef.current &&
      !loading &&
      (selectedSystems.length > 0 ||
        selectedDuctedSystems.length > 0 ||
        systemsToCompare.length > 0)
    ) {
      // Compare-tray chips must not outlive the quotable systems this wipe
      // clears — stranded chips leave Choose Systems looking populated with
      // a dead Next.
      resetInvalidatedSystemSelections(dispatch);
      notification('Selected systems reset', 'warning');
    }
  }, [
    structureOptionsKey,
    existingEquipmentKey,
    configurationOptionsKey,
    compositionPicksKey,
    projectOptionInputsKey
  ]);

  useEffect(() => {
    if (recommendationSelectionsKey === null) {
      recommendationSelectionsBaselineRef.current = null;
      return;
    }
    if (recommendationSelectionsBaselineRef.current === null) {
      recommendationSelectionsBaselineRef.current = recommendationSelectionsKey;
      return;
    }
    if (
      recommendationSelectionsBaselineRef.current ===
      recommendationSelectionsKey
    ) {
      return;
    }
    recommendationSelectionsBaselineRef.current = recommendationSelectionsKey;
    if (
      hasCompletedFirstMountRef.current &&
      !loading &&
      (selectedSystems.length > 0 ||
        selectedDuctedSystems.length > 0 ||
        systemsToCompare.length > 0)
    ) {
      resetInvalidatedSystemSelections(dispatch);
      notification('Selected systems reset', 'warning');
    }
  }, [recommendationSelectionsKey]);

  useEffect(() => {
    if (hasCompletedFirstMountRef.current && !loading) {
      selectedSystems.forEach((system) => {
        const hasSelectedAccessories =
          system.selectedAccessories.length > 0 ||
          system.configuration.condensers.some(
            (c) => c.selectedAccessories.length > 0
          );
        const computed = hasSelectedAccessories
          ? getQuotableAccessories(system)
          : undefined;

        dispatch(
          updateSelectedSystem({
            uniqueId: system.uniqueId,
            tax: undefined,
            shipping: undefined,
            rebates: undefined,
            quoteStatus: 'unsent',
            ...(computed !== undefined ? { quotableAccessories: computed } : {})
          })
        );
      });

      selectedDuctedSystems.forEach((system) => {
        const hasSelectedAccessories =
          system.selectedAccessories.length > 0 ||
          system.config.condensers.some(
            (c) => c.selectedAccessories.length > 0
          );
        const computed = hasSelectedAccessories
          ? getQuotableAccessories(system)
          : undefined;

        dispatch(
          updateSelectedDuctedSystem({
            id: system.id,
            tax: undefined,
            shipping: undefined,
            rebates: undefined,
            quoteStatus: 'unsent',
            ...(computed !== undefined ? { quotableAccessories: computed } : {})
          })
        );
      });
    }
  }, [
    JSON.stringify(
      selectedSystems.map((system) => ({
        ...system,
        tax: undefined,
        shipping: undefined,
        rebates: undefined,
        quoteStatus: undefined,
        quotableAccessories: undefined
      }))
    ),
    JSON.stringify(
      selectedDuctedSystems.map((system) => ({
        ...system,
        tax: undefined,
        shipping: undefined,
        rebates: undefined,
        quoteStatus: undefined,
        quotableAccessories: undefined
      }))
    )
  ]);

  useEffect(() => {
    dispatch(
      setActiveSystemType(
        selectedDuctedSystems.length > 0 ? 'ducted' : 'ductless'
      )
    );

    dispatch(setComparisonTableData(null));
  }, [activeTab]);

  useEffect(() => {
    if (
      hasCompletedFirstMountRef.current &&
      Object.values(systemOptions).some((opts) => opts.length > 0)
    ) {
      dispatch(setSystemOptions({}));
      dispatch(setSystemFilters(defaultUIState.systemFilters));
      notification('System options and filters reset', 'warning');
    }
  }, [
    JSON.stringify(
      configurations.map((config) => ({
        uniqueId: config.uniqueId,
        condensers: config.condensers.map((cond) => ({
          uniqueId: cond.uniqueId,
          zones: cond.zones.map((zone) => ({ uniqueId: zone.uniqueId }))
        }))
      }))
    )
  ]);

  useEffect(() => {
    dispatch(
      setSystemOptions(
        produce(systemOptions, (draft) => {
          Object.keys(draft).forEach((configId) => {
            draft[configId].forEach((option) => {
              const config = configurations.find(
                (c) => c.uniqueId === option.configuration.uniqueId
              );
              if (config) {
                option.configuration.name = config.name;
                option.configuration.condensers.forEach((cond) => {
                  const condenser = config.condensers.find(
                    (c) => c.uniqueId === cond.uniqueId
                  );
                  if (condenser) {
                    cond.name = condenser.name;
                    cond.zones.forEach((z) => {
                      const zone = condenser.zones.find(
                        (zz) => zz.uniqueId === z.uniqueId
                      );
                      if (zone) {
                        z.name = zone.name;
                      }
                    });
                  }
                });
              }
            });
          });
        })
      )
    );
  }, [
    JSON.stringify(
      configurations.map((config) => ({
        name: config.name,
        condensers: config.condensers.map((cond) => ({
          name: cond.name,
          zones: cond.zones.map((zone) => ({ name: zone.name }))
        }))
      }))
    )
  ]);

  useEffect(() => {
    if (!loading) {
      const systemPreference = getProjectSettingValue('system_preference');
      const previousPreference = previousSystemPreferenceRef.current;
      previousSystemPreferenceRef.current = systemPreference;

      // Ductless hides the replace-equipment question, so project_type
      // must be auto-set or the page can never complete (page-progress
      // requires it on every non-ducted path that does not ask).
      if (isEpOnlyPreference()) {
        dispatch(
          updateProjectSetting({
            attributeCode: 'project_type',
            value: 'Designing New System'
          })
        );
      }

      if (
        previousPreference !== undefined &&
        asksHasExistingEquipment(systemPreference) &&
        !asksHasExistingEquipment(previousPreference)
      ) {
        dispatch(
          updateProjectSetting({
            attributeCode: 'project_type',
            value: null
          })
        );
      }

      if (!isDuctedPathway()) {
        dispatch(
          updateProjectSetting({
            attributeCode: 'adding_supplemental_mini_splits',
            value: 'No'
          })
        );
      }

      if (
        previousPreference !== undefined &&
        previousPreference !== systemPreference
      ) {
        dispatch(resetPtacSelection());
      }

      // PTAC never asks about ducted equipment either, but its own anchor
      // system (the row the Choose Systems unit search anchors on) must
      // survive every mount — only the ducted systems go.
      if (isPtacSystemPreference(systemPreference)) {
        const remaining = existingSystems.filter(isPtacExistingSystem);
        if (remaining.length !== existingSystems.length) {
          dispatch(setExistingSystems(remaining));
        }
      }
    }
  }, [getProjectSettingValue('system_preference')]);

  useEffect(() => {
    if (
      getProjectSettingValue('project_type') === 'Designing New System' &&
      isEpOnlyPreference()
    ) {
      dispatch(setExistingSystems([]));
    }
  }, [getProjectSettingValue('project_type')]);

  useEffect(() => {
    if (getProjectSettingValue('adding_supplemental_mini_splits') === 'No') {
      dispatch(setSupplementalMiniSplitSpaces([]));
    }
  }, [getProjectSettingValue('adding_supplemental_mini_splits')]);

  useEffect(() => {
    if (loading) return;

    const selection = getProjectSettingValue('primary_or_detached_structure');

    if (selection === 'Primary Structure' && attomData) {
      const {
        suggestedStructureTypeId,
        suggestedStructureSubtypeId,
        suggestedStructureName
      } = store.getState().project;

      if (suggestedStructureTypeId !== null) {
        dispatch(setStructureTypeId(suggestedStructureTypeId));
        dispatch(setStructureSubtypeId(suggestedStructureSubtypeId));
        if (
          suggestedStructureName !== null &&
          !store.getState().structure.structureNameUserEdited
        ) {
          dispatch(setStructureName(suggestedStructureName));
        }
      }

      updatePropertyAttributesFromAttomData(attomData).then(() => {
        const updated = store.getState().project;
        if (updated.suggestedStructureTypeId !== null) {
          dispatch(setStructureTypeId(updated.suggestedStructureTypeId));
        }
        if (updated.suggestedStructureSubtypeId !== null) {
          dispatch(setStructureSubtypeId(updated.suggestedStructureSubtypeId));
        }
        if (
          updated.suggestedStructureName !== null &&
          !store.getState().structure.structureNameUserEdited
        ) {
          dispatch(setStructureName(updated.suggestedStructureName));
        }
      });
    }

    if (selection === 'Detached Structure' && attomData) {
      dispatch(setStructureTypeId(null));
      dispatch(setStructureSubtypeId(null));
      dispatch(setProjectSettingValue('square_footage', undefined));
      dispatch(setProjectSettingValue('number_of_floors', undefined));
      dispatch(setProjectSettingValue('number_of_bedrooms', undefined));
      dispatch(setProjectSettingValue('number_of_bathrooms', undefined));
      dispatch(setProjectSettingValue('number_of_fireplaces', undefined));
      dispatch(setProjectSettingValue('ductwork', undefined));
      dispatch(setProjectSettingValue('insulation_level', undefined));
      dispatch(setProjectSettingValue('foundation_type', undefined));
      dispatch(setProjectSettingValue('windows', undefined));
    }
  }, [getProjectSettingValue('primary_or_detached_structure')]);

  const validationAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    validationAbortRef.current?.abort();

    if (!activeConfiguration) {
      dispatch(setDesignRuleResults([]));
      return () => {};
    }

    if (!structureUniqueId) return () => {};

    const controller = new AbortController();
    validationAbortRef.current = controller;

    const designRulesRequestBody = {
      configurationUniqueIds: [activeConfiguration.uniqueId],
      configurations: [
        {
          ...activeConfiguration,
          condensers: activeConfiguration.condensers.map((c) => ({
            ...c,
            validSystems: []
          }))
        }
      ],
      structureUniqueId,
      systemPreference: getProjectSettingValue('system_preference') ?? null
    };

    axios
      .post<Record<string, DesignRuleValidationIssue[]>>(
        'design-rules/validate',
        designRulesRequestBody,
        { signal: controller.signal }
      )
      .then((response) => {
        const result = response.data[activeConfiguration.uniqueId] ?? [];
        dispatch(setDesignRuleResults(result));

        const valid = result.every((r) => !isErrorSeverity(r.severity));
        if (activeConfiguration.valid !== valid) {
          dispatch(
            syncConfigValid({
              uniqueId: activeConfiguration.uniqueId,
              valid
            })
          );
        }
      })
      .catch((error) => {
        if (error?.name === 'CanceledError') return;
        const previousIssues =
          store.getState().configurations.designRuleResults ?? [];
        if (previousIssues.length > 0) {
          console.warn(
            'design-rules/validate failed; keeping previous results visible.',
            error
          );
          return;
        }
        dispatch(
          setDesignRuleResults([
            {
              code: 'design-rules-unavailable',
              name: 'Design rules unavailable',
              severity: 'warning',
              message:
                'Design rule validation is temporarily unavailable. Please try again.',
              reasoning:
                'A network or server error prevented loading design rule results.',
              steps: [],
              recommendations: [],
              failures: []
            }
          ])
        );
      });

    return () => {
      validationAbortRef.current?.abort();
    };
  }, [
    activeConfiguration,
    structureUniqueId,
    getProjectSettingValue('system_preference')
  ]);

  // Keep the previous-render references up to date. Declared last so the
  // reset effects above still see the values from the previous render when
  // they run within the same commit.
  useEffect(() => {
    prevStructureRef.current = structure;
    prevConfigurationsRef.current = configurations;
    hasCompletedFirstMountRef.current = true;
  });

  return null;
}
